/**
 * Global Processing Subscriptions Hook
 * Manages Firestore subscriptions for files being processed
 * Keeps subscriptions alive across page navigation
 */

import { useEffect, useRef } from 'react'
import { useProcessingStore, useRecordingsStore } from '@/store/auth'
import { recordingsService } from '@/services/firebase.service'
import toast from 'react-hot-toast'

// Store for active subscriptions (outside React to persist)
const activeSubscriptions = new Map<string, () => void>()

export function useProcessingSubscriptions() {
  const { processingFiles, updateProcessingFile } = useProcessingStore()
  const { addRecording } = useRecordingsStore()
  const subscribedIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Subscribe to each processing file that doesn't have an active subscription
    processingFiles.forEach((file) => {
      // Skip if already subscribed or completed/error
      if (
        subscribedIds.current.has(file.id) ||
        file.status === 'completed' ||
        file.status === 'error'
      ) {
        return
      }

      // Skip if no recordingId yet (still uploading)
      if (!file.recordingId) {
        return
      }

      // Mark as subscribed
      subscribedIds.current.add(file.id)

      // Create Firestore subscription
      const unsubscribe = recordingsService.subscribe(file.recordingId, (recording) => {
        if (!recording) return

        // Update status based on Firestore data
        if (recording.status === 'transcribing') {
          updateProcessingFile(file.id, { status: 'transcribing' })
          toast.loading(`Trascrizione con Whisper...`, { id: file.id })
        } else if (recording.status === 'analyzing') {
          updateProcessingFile(file.id, { status: 'analyzing' })
          toast.loading(`Analisi con GPT-4...`, { id: file.id })
        } else if (recording.status === 'completed') {
          updateProcessingFile(file.id, { status: 'completed' })
          toast.success(`"${file.fileName}" completato!`, { id: file.id })

          // Add to recordings store
          addRecording({
            id: file.recordingId,
            title: file.fileName.replace(/\.[^/.]+$/, ''),
            fileName: file.fileName,
            fileSize: file.fileSize,
            duration: recording.duration || 0,
            status: 'completed',
            progress: 100,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })

          // Cleanup subscription
          subscribedIds.current.delete(file.id)
          activeSubscriptions.get(file.id)?.()
          activeSubscriptions.delete(file.id)
        } else if (recording.status === 'error') {
          updateProcessingFile(file.id, {
            status: 'error',
            error: recording.error || 'Errore durante elaborazione',
          })
          toast.error(`Errore: ${recording.error || 'Elaborazione fallita'}`, { id: file.id })

          // Cleanup subscription
          subscribedIds.current.delete(file.id)
          activeSubscriptions.get(file.id)?.()
          activeSubscriptions.delete(file.id)
        }
      })

      // Store subscription for cleanup
      activeSubscriptions.set(file.id, unsubscribe)
    })

    // Cleanup removed files
    return () => {
      // We don't cleanup on unmount - subscriptions persist!
      // They are cleaned up when completed/error
    }
  }, [processingFiles, updateProcessingFile, addRecording])

  // Return processing stats
  const processingCount = processingFiles.filter(
    (f) => f.status !== 'completed' && f.status !== 'error'
  ).length

  const hasProcessing = processingCount > 0

  return { processingCount, hasProcessing, processingFiles }
}

// Hook to use in components that don't need the full subscription logic
export function useProcessingStatus() {
  const { processingFiles } = useProcessingStore()

  const processingCount = processingFiles.filter(
    (f) => f.status !== 'completed' && f.status !== 'error'
  ).length

  const hasProcessing = processingCount > 0
  const hasErrors = processingFiles.some((f) => f.status === 'error')

  return { processingCount, hasProcessing, hasErrors, processingFiles }
}
